import apiClient from '@/lib/axios';
import { ENDPOINTS } from '@/services/api/endpoints';
import { resolveChatPersona } from '../utils/chatHelpers';
import { isSameChatUserId } from '../utils/chatUserId';

function getSidebarMetaForPersona(persona) {
    if (persona === 'executive' || persona === 'task-force-chair') {
        return {
            groupsTitle: 'Executive Announcements',
            directTitle: 'Private Chat',
        };
    }
    if (persona === 'project-manager') {
        return {
            groupsTitle: 'Pillar Chair Group Chat',
            directTitle: 'Private Chat',
        };
    }
    if (persona === 'member') {
        return {
            groupsTitle: 'Team chat',
            directTitle: 'Project Manager',
        };
    }
    return {
        groupsTitle: 'Group Chat',
        directTitle: 'Specific Chat',
    };
}

/**
 * Persona rules on the conversation list (mock + API safety net).
 * Member: no 1:1 with other members; team groups only when 3+ participants (PM + 2+ members).
 */
function filterConversationsForPersona(persona, list) {
    if (!Array.isArray(list)) return list;
    if (persona === 'project-manager') {
        return list.filter((c) => {
            // Keep project-scoped direct chats (PM <-> Pillar Chair).
            if (c?.kind === 'direct') return c?.scopeType === 'project';

            const name = String(c?.name ?? '').trim().toLowerCase();
            const subtitle = String(c?.subtitle ?? '').trim().toLowerCase();

            // Hide task-scoped threads from PM sidebar.
            if (name.startsWith('task:')) return false;
            if (subtitle === 'task-specific') return false;

            // Pillar-scoped groups are the PM's Pillar Chair channel — only keep the standard one
            // (backend renames "Project Managers" → "Pillar Chair - X"). Variant names like
            // "Venue (ILO) pillar chair" are excluded.
            if (c?.scopeType === 'pillar') return name.startsWith('pillar chair');

            // Filter out broadcast-style "Project managers" channels scoped at task_force or executive
            // level (e.g. exec → PM announcements). The pillar group is already preserved above.
            const pmToken = 'project manager';
            if (name.includes(pmToken) || name.includes(`${pmToken}s`)) return false;
            if (subtitle.includes(pmToken) || subtitle.includes(`${pmToken}s`)) return false;

            return true;
        });
    }
    if (persona === 'member') {
        return list.filter((c) => {
            if (c.kind === 'group') {
                // Members should always be able to see task-scoped threads even when they
                // only include the member + one role-holder (pillar chair or PM).
                const scopeType = String(c?.scopeType ?? c?.scope_type ?? '').trim();
                if (scopeType === 'task') return true;

                // Keep older heuristic for non-task team rooms.
                return (c.participantIds?.length ?? 0) > 2;
            }
            return true;
        });
    }
    return list;
}

/**
 * @param {object} params
 * @param {string|null} params.appRole
 * @param {string} params.userId
 * @param {object|null} [params.account]
 */
export async function fetchConversations({ appRole, userId, account, projectId, pillarId }) {
    const persona = resolveChatPersona(appRole, account);
    const metaFallback = getSidebarMetaForPersona(persona);
    const { data } = await apiClient.get(ENDPOINTS.CHAT.CONVERSATIONS, {
        params: {
            ...(projectId ? { projectId } : {}),
            ...(pillarId ? { pillarId } : {}),
        },
    });
    /** Backend may return `{ conversations, meta }` or a flat list */
    if (data && typeof data === 'object' && Array.isArray(data.conversations)) {
        return {
            data: filterConversationsForPersona(persona, data.conversations),
            meta: data.meta ?? metaFallback,
        };
    }
    if (Array.isArray(data)) {
        return {
            data: filterConversationsForPersona(persona, data),
            meta: metaFallback,
        };
    }
    return { data, meta: metaFallback };
}

/**
 * @param {string} conversationId
 * @param {string} currentUserId
 */
export async function fetchMessages(conversationId, currentUserId) {
    const { data } = await apiClient.get(ENDPOINTS.CHAT.CONVERSATION_MESSAGES(conversationId));
    const list = Array.isArray(data) ? data : [];
    return {
        data: list.map((m) => {
            const sid = m.senderId ?? m.sender_id;
            const aid = m.authorId ?? m.author_id ?? sid;
            const senderId = String(sid ?? '');
            const authorId = String(aid ?? '');
            const isOwn = isSameChatUserId(authorId, currentUserId);
            return { ...m, senderId, authorId, isOwn };
        }),
    };
}

/**
 * @param {object} params
 * @param {string} params.conversationId
 * @param {string} params.body
 * @param {string} params.senderId
 * @param {string} params.senderName
 */
export async function sendMessageApi({ conversationId, body, senderId, senderName }) {
    const { data } = await apiClient.post(ENDPOINTS.CHAT.CONVERSATION_MESSAGES(conversationId), {
        body,
    });
    return { data };
}

/**
 * @param {string} conversationId
 */
export async function markConversationReadApi(conversationId) {
    await apiClient.post(ENDPOINTS.CHAT.MARK_READ(conversationId));
    return { ok: true };
}

/**
 * @param {string} query
 * @returns {Promise<Array<{ pillarId: string, pillarName: string, taskForceName: string, chairUserId: string, chairName: string }>>}
 */
export async function searchPillars(query) {
    const q = typeof query === 'string' ? query.trim() : '';
    if (!q) return [];
    const { data } = await apiClient.get(ENDPOINTS.CHAT.SEARCH_PILLARS, {
        params: { q },
    });
    return Array.isArray(data?.results) ? data.results : [];
}

/**
 * @param {string} query
 * @returns {Promise<Array<{ taskForceId: string, taskForceName: string, chairUserId: string, chairName: string }>>}
 */
export async function searchTaskforces(query) {
    const q = typeof query === 'string' ? query.trim() : '';
    if (!q) return [];
    const { data } = await apiClient.get(ENDPOINTS.CHAT.SEARCH_TASKFORCES, {
        params: { q },
    });
    return Array.isArray(data?.results) ? data.results : [];
}

/**
 * @param {string} targetUserId
 */
export async function createOrGetDirectConversation(targetUserId) {
    const { data } = await apiClient.post(ENDPOINTS.CHAT.DIRECT, {
        targetUserId,
    });
    return { data };
}
